import { useState } from 'react';
import { Button, Textarea } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { ConfirmDialog } from '@vnetwork/ui/feedback';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useRenewContract,
  useTerminateContract,
  useRetainContract,
  type ContractDetail,
} from '@/api/contracts';
import { DateInput } from '@/components/date-input';

type ActionKind = 'renew' | 'terminate' | 'retain' | null;

/** Build a local `YYYY-MM-DD` string without UTC shift (Asia/Saigon safe). */
function toLocalDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Lifecycle actions for an active contract — Renew / Terminate / Retain. Only
 * rendered for active contracts; success/error surface via the toast.
 *
 * Renew collects new effective/expiry dates + renewal_reason (all required by the
 * backend). Retain and Terminate each require a reason string.
 */
export function ContractActions({ detail }: { detail: ContractDetail }) {
  const toast = useToast();
  const [open, setOpen] = useState<ActionKind>(null);

  // Renew form state
  const [renewEffective, setRenewEffective] = useState(
    () => toLocalDateString(new Date()),
  );
  const [renewExpiry, setRenewExpiry] = useState('');
  const [renewReason, setRenewReason] = useState('');
  const [renewErrors, setRenewErrors] = useState<Record<string, string>>({});

  // Retain/Terminate reason state
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(false);

  const renew = useRenewContract(detail.contract_id);
  const terminate = useTerminateContract(detail.contract_id);
  const retain = useRetainContract(detail.contract_id);

  if (detail.contract_status !== 'active') return null;

  const close = () => {
    setOpen(null);
    setReason('');
    setReasonError(false);
    setRenewEffective(toLocalDateString(new Date()));
    setRenewExpiry('');
    setRenewReason('');
    setRenewErrors({});
  };

  const onError = (label: string) => (err: unknown) => {
    const message = err instanceof Error ? err.message : `Failed to ${label} contract`;
    toast.error(`Failed to ${label} contract`, { description: message });
  };
  const onDone = (label: string) => () => {
    toast.success(`Contract ${label}`);
    close();
  };

  /** Retain / Terminate both require a non-empty reason. */
  const runWithReason = (kind: 'retain' | 'terminate') => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setReasonError(true);
      return;
    }
    const mutation = kind === 'retain' ? retain : terminate;
    const label = kind === 'retain' ? 'retained' : 'terminated';
    mutation.mutate({ reason: trimmed }, { onSuccess: onDone(label), onError: onError(kind) });
  };

  /**
   * Renew — collects effective date, expiry date, and renewal_reason.
   * All three are required by the backend (`binding:"required"`).
   */
  const runRenew = () => {
    const errs: Record<string, string> = {};
    if (!renewEffective) errs.effective = 'Effective date is required.';
    if (!renewExpiry) errs.expiry = 'Expiry date is required.';
    else if (renewExpiry <= renewEffective) errs.expiry = 'Expiry date must be after effective date.';
    if (!renewReason.trim()) errs.reason = 'Renewal reason is required.';
    if (Object.keys(errs).length) {
      setRenewErrors(errs);
      return;
    }
    renew.mutate(
      {
        contract_effective_date: renewEffective,
        contract_expiry_date: renewExpiry,
        renewal_reason: renewReason.trim(),
      },
      { onSuccess: onDone('renewed'), onError: onError('renew') },
    );
  };

  const reasonField = (
    <Field
      label="Reason"
      description="Recorded on the contract's change history."
      error={reasonError ? 'A reason is required.' : undefined}
    >
      {(fieldProps) => (
        <Textarea
          {...fieldProps}
          rows={3}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (reasonError) setReasonError(false);
          }}
          placeholder="Reason for this action"
        />
      )}
    </Field>
  );

  const renewForm = (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      <Field
        label="New effective date"
        error={renewErrors.effective}
      >
        {(fieldProps) => (
          <DateInput
            {...fieldProps}
            value={renewEffective}
            onChange={(e) => {
              setRenewEffective(e.target.value);
              setRenewErrors((prev) => ({ ...prev, effective: '' }));
            }}
          />
        )}
      </Field>
      <Field
        label="New expiry date"
        error={renewErrors.expiry}
      >
        {(fieldProps) => (
          <DateInput
            {...fieldProps}
            value={renewExpiry}
            onChange={(e) => {
              setRenewExpiry(e.target.value);
              setRenewErrors((prev) => ({ ...prev, expiry: '' }));
            }}
          />
        )}
      </Field>
      <Field
        label="Renewal reason"
        error={renewErrors.reason}
      >
        {(fieldProps) => (
          <Textarea
            {...fieldProps}
            rows={3}
            value={renewReason}
            onChange={(e) => {
              setRenewReason(e.target.value);
              setRenewErrors((prev) => ({ ...prev, reason: '' }));
            }}
            placeholder="Reason for renewing this contract"
          />
        )}
      </Field>
    </div>
  );

  return (
    <>
      <div style={{ display: 'flex', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
        <Button size="sm" variant="secondary" onClick={() => setOpen('renew')}>
          Renew
        </Button>
        <Button size="sm" variant="secondary" onClick={() => setOpen('retain')}>
          Retain
        </Button>
        <Button size="sm" variant="danger" onClick={() => setOpen('terminate')}>
          Terminate
        </Button>
      </div>

      <ConfirmDialog
        open={open === 'renew'}
        onOpenChange={(o) => (o ? setOpen('renew') : close())}
        title="Renew contract"
        description="Set the new contract term dates and provide a renewal reason."
        confirmLabel="Renew"
        loading={renew.isPending}
        onConfirm={runRenew}
      >
        {renewForm}
      </ConfirmDialog>

      <ConfirmDialog
        open={open === 'retain'}
        onOpenChange={(o) => (o ? setOpen('retain') : close())}
        title="Retain contract"
        description="Clone this contract as a retained record with the reason below."
        confirmLabel="Retain"
        loading={retain.isPending}
        onConfirm={() => runWithReason('retain')}
      >
        {reasonField}
      </ConfirmDialog>

      <ConfirmDialog
        open={open === 'terminate'}
        onOpenChange={(o) => (o ? setOpen('terminate') : close())}
        title="Terminate contract"
        description="This ends the contract. Provide a termination reason."
        confirmLabel="Terminate"
        destructive
        loading={terminate.isPending}
        onConfirm={() => runWithReason('terminate')}
      >
        {reasonField}
      </ConfirmDialog>
    </>
  );
}
